"use client";

import OverlayWrapper from "@/components/ui/OverLayWrapper";
import { CustomModal } from "@/components/ui/CustomModal";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import clsx from "clsx";
import { ReactNode } from "react";
import { AlertTriangle } from "lucide-react";

type ConfirmOverlayProps = {
  onCancel: () => void;
  onConfirm: () => void;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "danger" | "warning" | "default";
  disableActions?: boolean;
  heightClassName?: number | `${number}vh` | `${number}%`;
  showCancel?: boolean;
};

export function ConfirmOverlay({
  onCancel,
  onConfirm,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "default",
  disableActions = false,
  heightClassName = "40vh",
  showCancel = true,
}: ConfirmOverlayProps) {
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const handleClose = () => {
    if (!disableActions) {
      onCancel();
    }
  };

  const isDanger = variant === "danger";
  const isWarning = variant === "warning";
  const showAlertIcon = isDanger || isWarning;
  const body = (
    <div className="flex h-full flex-col gap-6 px-5 pt-4 pb-6">
      <div className="relative flex items-center justify-between">
        {showCancel ? (
          <button
            type="button"
            className="hover:border-slateCustom rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition disabled:cursor-not-allowed disabled:opacity-60"
            onClick={onCancel}
            disabled={disableActions}
          >
            {cancelLabel}
          </button>
        ) : (
          <span aria-hidden="true" />
        )}

        {showAlertIcon ? (
          <div
            className={clsx(
              "absolute left-1/2 flex h-10 w-10 -translate-x-1/2 items-center justify-center rounded-full",
              isDanger
                ? "bg-red-50 text-red-600"
                : "bg-amber-50 text-amber-600",
            )}
          >
            <AlertTriangle size={20} />
          </div>
        ) : null}

        <button
          type="button"
          className={clsx(
            "rounded-lg px-4 py-2 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-60",
            isDanger
              ? "bg-red-600 hover:bg-red-500"
              : isWarning
                ? "bg-amber-600 hover:bg-amber-500"
                : "bg-slateCustom hover:opacity-90",
          )}
          onClick={onConfirm}
          disabled={disableActions}
        >
          {confirmLabel}
        </button>
      </div>

      <div className="rounded-xl bg-white p-4 text-center shadow-sm ring-1 ring-gray-100">
        <div className="text-base font-semibold text-gray-900">{title}</div>
        <div className="mt-2 text-sm text-gray-600">{message}</div>
      </div>
    </div>
  );

  if (isDesktop) {
    return (
      <CustomModal
        open
        onOpenChange={(next) => {
          if (!next) handleClose();
        }}
        closeOnOutsideClick={!disableActions}
        maxWidth="max-w-md"
        centered
        bodyClassName="p-0"
      >
        {body}
      </CustomModal>
    );
  }

  return (
    <OverlayWrapper
      onClose={handleClose}
      snapPoints={[heightClassName]}
      draggable={false}
    >
      {body}
    </OverlayWrapper>
  );
}
